import { useState } from 'react'
import { Star, ThumbsUp, ChevronDown, Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectItem } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { EmployeeProfile } from '@/types'

interface ReviewsSectionProps {
  employee: EmployeeProfile
}

type SortOption = 'newest' | 'oldest' | 'rating-high' | 'rating-low' | 'helpful'

/**
 * Reviews Section Component
 * 
 * Comprehensive review and rating system featuring:
 * - Rating overview with distribution
 * - Individual user reviews
 * - Sorting and filtering options
 * - Pagination for large datasets
 * - Helpful votes system
 */
export function ReviewsSection({ employee }: ReviewsSectionProps) {
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [showAllReviews, setShowAllReviews] = useState(false)
  const [helpfulVotes, setHelpfulVotes] = useState<Record<string, boolean>>({})

  const reviews = employee.reviews
  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3)

  const sortedReviews = [...displayedReviews].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return b.createdAt.getTime() - a.createdAt.getTime()
      case 'oldest':
        return a.createdAt.getTime() - b.createdAt.getTime()
      case 'rating-high':
        return b.rating - a.rating
      case 'rating-low':
        return a.rating - b.rating
      case 'helpful':
        return b.helpful - a.helpful
      default:
        return 0
    }
  })

  const renderStars = (rating: number, size: 'sm' | 'md' = 'sm') => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={cn(
          size === 'sm' ? 'w-4 h-4' : 'w-5 h-5',
          i < Math.floor(rating)
            ? 'text-yellow-400 fill-yellow-400'
            : 'text-gray-300'
        )}
      />
    ))
  }

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
    reviews.forEach(review => {
      distribution[review.rating as keyof typeof distribution]++
    })
    return distribution
  }

  const handleHelpfulVote = (reviewId: string) => {
    setHelpfulVotes(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }))
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const distribution = getRatingDistribution()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5" />
          Reviews & Ratings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Rating Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Average Rating */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              {renderStars(employee.averageRating, 'md')}
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">
              {employee.averageRating.toFixed(1)}
            </div>
            <div className="text-sm text-muted-foreground">
              Based on {employee.totalReviews} reviews
            </div>
          </div>

          {/* Rating Distribution */}
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((stars) => (
              <div key={stars} className="flex items-center gap-2 text-sm">
                <span className="w-8 text-muted-foreground">{stars}★</span>
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div
                    className="bg-yellow-400 h-2 rounded-full"
                    style={{
                      width: `${(distribution[stars as keyof typeof distribution] / employee.totalReviews) * 100}%`
                    }}
                  />
                </div>
                <span className="w-8 text-muted-foreground text-right">
                  {distribution[stars as keyof typeof distribution]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Sorting Options */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <Select
            value={sortBy}
            onValueChange={(value) => setSortBy(value as SortOption)}
          >
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
            <SelectItem value="rating-high">Highest Rating</SelectItem>
            <SelectItem value="rating-low">Lowest Rating</SelectItem>
            <SelectItem value="helpful">Most Helpful</SelectItem>
          </Select>
        </div>

        {/* Reviews List */}
        <div className="space-y-3">
          {sortedReviews.map((review) => (
            <div
              key={review.id}
              className="p-2 rounded-lg space-y-3"
            >
              {/* Review Header */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center text-sm font-medium overflow-hidden">
                  {review.userAvatar ? (
                    <img
                      src={review.userAvatar}
                      alt={review.userName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    review.userName.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-foreground">
                      {review.userName}
                    </span>
                    {review.verified && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {renderStars(review.rating)}
                    <span className="text-xs text-muted-foreground">
                      {formatDate(review.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Review Content */}
              <p className="text-sm text-muted-foreground leading-relaxed">
                {review.comment}
              </p>

              {/* Review Actions */}
              <div className="flex items-center gap-4 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleHelpfulVote(review.id)}
                  className={cn(
                    "text-xs",
                    helpfulVotes[review.id]
                      ? "text-primary"
                      : "text-muted-foreground"
                  )}
                >
                  <ThumbsUp className="w-3 h-3 mr-1" />
                  Helpful ({review.helpful + (helpfulVotes[review.id] ? 1 : 0)})
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Show More/Less Button */}
        {reviews.length > 3 && (
          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => setShowAllReviews(!showAllReviews)}
              className="flex items-center gap-2"
            >
              {showAllReviews ? 'Show Less' : `Show All ${reviews.length} Reviews`}
              <ChevronDown
                className={cn(
                  'w-4 h-4 transition-transform',
                  showAllReviews && 'rotate-180'
                )}
              />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

ReviewsSection.displayName = "ReviewsSection"